/**
 * @file LastXeroSync.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-24
 * @modified 2026-08-24
 * @description "Last Xero sync: …" for a search screen's info note.
 *
 *              One component rather than the same fetch-and-format pasted into
 *              five screens. Renders nothing at all until it has a date, so a
 *              slow or failed lookup leaves the surrounding note reading
 *              normally instead of showing "Last Xero sync: -".
 *
 *              Time is Europe/London, not the viewer's zone: this reports when
 *              the system last did something, and that happens on the office's
 *              clock.
 *
 *              Usage, inside an existing `.note`:
 *                <LastXeroSync entity="purchase_orders" />
 */

import React, { useState, useEffect } from 'react';
import { API_URL } from '../config';
import { formatDateTimeLondon } from '../utils/dateFormatters';

// Must match LAST_SYNC_SOURCES in routes/sync.js
export const LAST_SYNC_ENTITIES = ['purchase_orders', 'contacts', 'invoices', 'bills', 'inventory_items'];

function LastXeroSync({ entity, prefix = 'Last Xero sync' }) {
  const [lastSync, setLastSync] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/sync/last-sync/${entity}`, { credentials: 'include' });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setLastSync(data.lastSync || null);
      } catch {
        // A missing timestamp is not worth an error to the user — the note
        // simply carries on without it.
      }
    })();
    return () => { cancelled = true; };
  }, [entity]);

  if (!lastSync) return null;

  return <> {prefix}: {formatDateTimeLondon(lastSync)}.</>;
}

export default LastXeroSync;
